import React, { useState } from "react";
import { Calendar, dayjsLocalizer, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import dayjs from "dayjs";
import "dayjs/locale/es"; // Importa el idioma español para dayjs
import { Button, Box, Modal, Typography, Card, Tab, Tabs, Stack, Grid, useTheme, useMediaQuery } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DACIPagination from "components/DACIPagination";
import DACITabs from "components/DACITabs";
import { useNavigate } from "react-router-dom";
import usePermission from "hooks/usePermission";
import PropTypes, { string } from "prop-types";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useMaterialUIController } from "context";
import colors from "assets/theme/base/colors";
import { useMemo } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

// Configura dayjs para usar español
dayjs.locale("es");

// Configuración del localizador de fechas en español
const localizer = dayjsLocalizer(dayjs);

// Mensajes en español para el calendario
const messages = {
  allDay: "Todo el día",
  previous: "Anterior",
  next: "Siguiente",
  today: "Hoy",
  month: "Mes",
  week: "Semana",
  day: "Día",
  agenda: "Agenda",
  date: "Fecha",
  time: "Hora",
  event: "Evento",
  noEventsInRange: "No hay eventos en este rango.",
  showMore: (total) => `+ Ver más (${total})`,
};

const ActivitiesList = ({
  handleActivitiesList = [],
  handleActivitiesCreate,
  handleActivitiesDelete,
  handleActivitiesUpdate,
  handleActivitiesEdit, // Nueva prop para manejar edición
  pageIndexAndPageSize,
  entriesStart,
  entriesEnd,
  totalData,
  totalPages,
  currentPage,
  handlePageChange,
  callHandleClickSubmit,
  valueTab,
  handleChangeTab,
  initialLoading = false,
  nodesList = [],
}) => {

  let { white, dark, black } = colors;

  const textColor = dark.main;
  const modalTextColor = black.main;
  white = white.main;

  // Obtener el estado del tema (modo oscuro/claro)
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const navigate = useNavigate();
  // Decodifica entidades HTML como &lt; &gt; &amp; etc. a sus caracteres correspondientes
  const decodeHtml = (input) => {
    if (input === null || input === undefined) return input;
    if (typeof input !== "string") return input;
    // Usar DOM cuando esté disponible (cliente)
    if (typeof window !== "undefined" && window.document) {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = input;
      return textarea.value;
    }
    // Fallback simple
    return input
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'");
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentView, setCurrentView] = useState(Views.AGENDA); // Guarda la vista actual

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 960px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // < 600px

  // Verificar permisos
  const canCreate = usePermission("Crear", "Actividades");
  const canEdit = usePermission("Editar", "Actividades");
  const canDelete = usePermission("Eliminar", "Actividades");
  const canListEvidence = usePermission("Listar", "Evidencias");
  const canListParticipant = usePermission("Listar", "Participantes");

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "Fecha inválida" : date.toISOString().slice(0, 10);
  };

  function formatDateT(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1; // Los meses en JavaScript son 0-indexados
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const handleEventClick = (event, module) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  const handleNavigate = (path, title) => {
    navigate(path, { state: { activityTitle: title } });
    handleCloseModal();
  };

  const events =
    handleActivitiesList && Array.isArray(handleActivitiesList)
      ? handleActivitiesList.map((userMan) => {
        // Separar fecha y hora
        const startDate = new Date(userMan.fecha);
        const endDate = userMan.fecha_fin
          ? new Date(userMan.fecha_fin)
          : new Date(startDate.getTime() + 60 * 60 * 1000);

        // Si `timeStart` o `timeEnd` están definidos, usarlos directamente
        const timeStart = userMan.timeStart
          ? userMan.timeStart
          : startDate.toLocaleTimeString("es-ES", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "America/Bogota", // Ajusta a tu zona horaria
          });

        const timeEnd = userMan.timeEnd
          ? userMan.timeEnd
          : new Date(startDate.getTime() + 60 * 60 * 1000).toLocaleTimeString("es-ES", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "America/Bogota",
          });

        return {
          id: userMan.id,
          title: decodeHtml(userMan.title),
          fecha: formatDateT(userMan.fecha),
          start: startDate,
          end: endDate,
          timeStart: timeStart,
          timeEnd: timeEnd,
          description: decodeHtml(userMan.description),
          subcategories: Array.isArray(userMan.subcategories)
            ? userMan.subcategories.map((item) => {
              // Manejar diferentes estructuras de datos
              if (typeof item === 'string') return item;
              if (item.subcategory && typeof item.subcategory === 'object' && item.subcategory.name) {
                return item.subcategory.name;
              }
              if (item.name) return item.name;
              return 'Sin nombre';
            }).filter(name => name && name !== 'Sin nombre').join(", ")
            : typeof userMan.subcategories === 'string' ? userMan.subcategories : "",
          evidence_formats: userMan.evidence_formats,
          keywords: userMan.keywords,
          participants: userMan.participants,
          is_certifiable: userMan.is_certifiable,
          node: userMan.node,
          node_id: userMan.node_id,
          node_name: userMan.node_name,
          node_municipality: userMan.node_municipality,
          author_name: userMan.author_name,
          author_email: userMan.author_email,
          author_full_info: userMan.author_full_info,
        };
      })
      : [];

  // Modal para mostrar detalles del evento (solo en vista "Mes", "Semana", etc.)
  const EventModal = ({ open, onClose, event }) => {
    const style = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "400px",
      borderRadius: "20px",
      boxShadow: 24,
      p: 4,
    };

    // Función para renderizar valores de forma segura
    const safeRender = (value) => {
      if (value === null || value === undefined || value === '') return 'N/A';
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          const filtered = value.map(item => {
            if (typeof item === 'string') return item;
            if (item.subcategory && item.subcategory.name) return item.subcategory.name;
            if (item.name) return item.name;
            return '';
          }).filter(Boolean);
          return filtered.length > 0 ? filtered.join(', ') : 'N/A';
        }
        return value.name || value.code || 'N/A';
      }
      return String(value) || 'N/A';
    };

    return (
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description">
        <Card sx={{
          ...style,
          backgroundColor: darkMode ? "#202940" : "#ffffff",
        }}>
          <MDBox
            mt={-7}
            color="white"
            bgColor="info"
            variant="gradient"
            borderRadius="lg"
            p={2}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography id="modal-title" component="h2" sx={{ color: "white" }}>
                Detalles del evento
              </Typography>
              {canEdit && (
                <Box
                  sx={{
                    cursor: "pointer",
                    padding: "6px",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                  onClick={() => {
                    if (handleActivitiesEdit) {
                      handleActivitiesEdit(event);
                      onClose(); // Cerrar el modal de detalles al abrir el de edición
                    }
                  }}
                  title="Editar actividad">
                  <EditOutlinedIcon
                    sx={{
                      fontSize: "20px",
                      color: "white"
                    }}
                  />
                </Box>
              )}
            </Box>
            <Button
              sx={{
                fontSize: "1.5rem",
                color: "white",
                minWidth: "unset",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                padding: 0,
              }}
              onClick={onClose}>
              <span style={{ fontSize: "1.5rem", color: "white" }}>×</span>
            </Button>
          </MDBox>
          <Box mt={2}>
            <Typography variant="body1" sx={{ color: darkMode ? white : modalTextColor }}>
              <strong>Título:</strong> <br />
              <span
                title={event.title}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                {safeRender(event.title)}
              </span>
            </Typography>
            <Typography variant="body1" sx={{ color: darkMode ? white : modalTextColor }}>
              <strong>Nodo:</strong> <br />
              {event.node_name
                ? `${event.node_name}${event.node_municipality ? ` - ${event.node_municipality}` : ""}`
                : 'N/A'}
            </Typography>
            <Typography variant="body1" sx={{ color: darkMode ? white : modalTextColor }}>
              <strong>Subcategorías:</strong> <br />
              <span
                title={safeRender(event.subcategories)}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                {safeRender(event.subcategories)}
              </span>
            </Typography>
            <Typography variant="body1" sx={{ color: darkMode ? white : modalTextColor }}>
              <strong>Descripción:</strong> <br />
              <span
                title={safeRender(event.description)}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                {safeRender(event.description)}
              </span>
            </Typography>
            <Typography variant="body1" sx={{ color: darkMode ? white : modalTextColor }}>
              <strong>Palabra clave:</strong> <br />
              <span
                title={safeRender(event.keywords)}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                {safeRender(event.keywords)}
              </span>
            </Typography>
            <Typography variant="body1" sx={{ color: darkMode ? white : modalTextColor }}>
              <strong>Autor:</strong> <br />
              {event.author_full_info || event.author_name || "N/A"}
            </Typography>
            <Typography variant="body1" sx={{ color: darkMode ? white : modalTextColor }}>
              <strong>Fecha de actividad:</strong> <br />
              {event.fecha && event.fecha.includes && event.fecha.includes("T") ? formatDateT(event.fecha) : (event.fecha || 'N/A')}
            </Typography>
            <Typography variant="body1" sx={{ color: darkMode ? white : modalTextColor }}>
              <strong>Certificable:</strong> <br />
              {event.is_certifiable ? "Sí" : "No"}
            </Typography>
            <Box mt={2} display="flex" gap={1}>
              {canListEvidence && (
                <MDButton
                  variant="contained"
                  color="info"
                  onClick={() => {

                    handleNavigate(`/Actividades-E/${event.id}`, event.title);
                  }}>
                  Ver evidencias
                </MDButton>
              )}
              {canListParticipant && (
                <MDButton
                  variant="contained"
                  color="info"
                  onClick={() =>
                    navigate(`/Actividades-P/${event.id}`, {
                      state: { activityTitle: event.title },
                    })
                  }>
                  Ver participantes
                </MDButton>
              )}
            </Box>
          </Box>
        </Card>
      </Modal>
    );
  };

  const AgendaEvent = ({ event }) => (
    <MDBox
      display="grid"
      gridTemplateColumns="repeat(5, 1fr)" // Volver a 5 columnas
      gap={2}
      padding="10px"
      borderBottom={darkMode ? "1px solid #616161" : "1px solid #e0e0e0"}
      onClick={() => handleEventClick(event)}
      sx={{
        cursor: "pointer",
        "&:hover": {
          backgroundColor: darkMode ? "#2d3748" : "#f5f5f5"
        }
      }}>
      <Typography variant="body2" sx={{ color: darkMode ? white : textColor }}>
        <strong>Título:</strong> <br />
        {event.title || 'N/A'}
      </Typography>
      <Typography variant="body2" sx={{ color: darkMode ? white : textColor }}>
        <strong>Nodo:</strong> <br />
        {event.node_name
          ? `${event.node_name}${event.node_municipality ? ` - ${event.node_municipality}` : ""}`
          : 'N/A'}
      </Typography>
      <Typography variant="body2" sx={{ color: darkMode ? white : textColor }}>
        <strong>Subcategorías:</strong> <br />
        {event.subcategories || 'N/A'}
      </Typography>
      <Typography variant="body2" sx={{ color: darkMode ? white : textColor }}>
        <strong>Descripción:</strong> <br />
        {event.description || 'N/A'}
      </Typography>
      <Typography variant="body2" sx={{ color: darkMode ? white : textColor }}>
        <strong>Palabras clave:</strong> <br />
        {event.keywords || 'N/A'}
      </Typography>
      <Typography variant="body2" sx={{ color: darkMode ? white : textColor }}>
        <strong>Certificable:</strong> <br />
        {event.is_certifiable ? "Sí" : "No"}
      </Typography>
      <Typography variant="body2" sx={{ color: darkMode ? white : textColor }}>
        <strong>Autor:</strong> <br />
        {event.author_email || 'N/A'}
      </Typography>
      <MDBox display="flex" gap={1}>
        {canListEvidence && (
          <MDButton
            variant="contained"
            color="info"
            onClick={() => {

              handleNavigate(`/Actividades-E/${event.id}`, event.title);
            }}>
            Ver evidencia
          </MDButton>
        )}
        {canListParticipant && (
          <MDButton
            variant="contained"
            color="info"
            onClick={() =>
              navigate(`/Actividades-P/${event.id}`, { state: { activityTitle: event.title } })
            }>
            Ver Participantes
          </MDButton>
        )}
      </MDBox>
    </MDBox >
  );

  // Memoizar los datos de la tabla para evitar bucles infinitos
  const tableData = useMemo(
    () => ({
      columns: [
        { Header: "Título", accessor: "title", width: "15%" },
        { Header: "Fecha", accessor: "fecha", width: "10%" },
        { Header: "Nodo", accessor: "node", width: "12%" },
        {
          Header: "Descripción",
          accessor: "description",
          Cell: ({ value }) => (
            <div
              title={value}
              style={{
                width: "100%",
                maxWidth: "200px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
              {value}
            </div>
          ),
          width: "15%",
        },
        { Header: "Palabras clave", accessor: "keywords", width: "12%" },
        { Header: "Autor", accessor: "author", width: "15%" },
        { Header: "Certificable", accessor: "is_certifiable", width: "8%" },
        {
          Header: "Acciones",
          accessor: "action",
          width: "8%",
          align: "center",
          disableSortBy: true,
        },
      ],
      rows: handleActivitiesList
        ? handleActivitiesList.map((activity) => {
          return {
            id: activity.id,
            title: decodeHtml(activity.title) || 'N/A',
            fecha: formatDate(activity.fecha) || 'N/A',
            node: activity.node_name
              ? `${activity.node_name}${activity.node_municipality ? ` - ${activity.node_municipality}` : ""}`
              : 'N/A',
            description: decodeHtml(activity.description) || 'N/A',
            keywords: activity.keywords || 'N/A',
            author: activity.author_email || 'N/A',
            is_certifiable: activity.is_certifiable ? "Sí" : "No",
            action: (
              <>
                <button
                  type="button"
                  aria-label={`Ver detalles actividad ${activity.id}`}
                  style={{
                    cursor: "pointer",
                    background: "transparent",
                    border: "none",
                    padding: 4,
                    color: "#81858dff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => {
                    setCurrentView("table");
                    handleEventClick(activity, "table");
                  }}>
                  <VisibilityOutlinedIcon
                    titleAccess="Ver detalles"
                    style={{
                      color: "inherit", // toma el gris definido en el botón
                      fontSize: "20px",
                      width: "24px",
                      height: "24px",
                    }}
                  />
                </button>
                {canEdit && (
                  <button
                    type="button"
                    aria-label={`Editar actividad ${activity.id}`}
                    style={{
                      cursor: "pointer",
                      background: "transparent",
                      border: "none",
                      padding: 4,
                      color: "#81858dff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => {
                      if (handleActivitiesEdit) {
                        handleActivitiesEdit(activity);
                      }
                    }}>
                    <EditOutlinedIcon
                      titleAccess="Editar actividad"
                      style={{
                        color: "inherit",
                        fontSize: "20px",
                        width: "24px",
                        height: "24px",
                      }}
                    />
                  </button>
                )}
              </>
            ),
          };
        })
        : [],
    }),
    [handleActivitiesList]
  );

  return (
    <div>
      <Box sx={{
        mt: isMobile ? 2 : -6,
        mb: 3,
        px: { xs: 1, sm: 2, md: 3 },
        width: '100%'
      }}>
        {/* Contenedor para el botón "Crear" */}
        {/* Grid responsivo para botones de acción */}
        <Grid container spacing={1} justifyContent={isMobile ? "center" : "flex-end"} alignItems="center" sx={{ gap: 0 }}>
          {canCreate && (
            <Grid item>
              <MDButton onClick={handleActivitiesCreate} variant="contained" color="info">
                Crear
              </MDButton>
            </Grid>
          )}
          {canCreate && (
            <Grid item>
              <MDButton
                onClick={callHandleClickSubmit}
                data-export={true}
                variant="contained"
                color="info">
                Exportar
              </MDButton>
            </Grid>
          )}
        </Grid>
      </Box>
      {/* Espacio entre el botón y el calendario */}
      <br />
      <DACITabs
        value={valueTab}
        onChange={handleChangeTab}
        tabs={[
          { value: 0, label: "Vista tipo calendario", wrapped: true },
          { value: 1, label: "Vista tipo tabla", wrapped: false }
        ]}
      />
      {/* Contenedor para el calendario */}
      {valueTab === 0 && (
        <>
          <MDBox
            style={{ padding: "20px" }}
            sx={{
              "& .rbc-calendar": {
                backgroundColor: darkMode ? "#202940" : "#ffffff",
                color: darkMode ? "#ffffff" : textColor,
              },
              "& .rbc-header": {
                backgroundColor: darkMode ? "#1a2035" : "#f5f5f5",
                color: darkMode ? "#ffffff !important" : `${textColor} !important`,
                borderColor: darkMode ? "#616161" : "#e0e0e0",
              },
              "& .rbc-toolbar": {
                "& button": {
                  backgroundColor: darkMode ? "#1a2035" : "#ffffff",
                  color: darkMode ? "#ffffff !important" : `${textColor} !important`,
                  borderColor: darkMode ? "#616161" : "#e0e0e0",
                  "&:hover": {
                    backgroundColor: darkMode ? "#2d3748" : "#f5f5f5",
                  },
                  "&.rbc-active": {
                    backgroundColor: darkMode ? "#2d3748" : "#e0e0e0",
                  }
                },
                "& .rbc-btn-group button": {
                  backgroundColor: darkMode ? "#1a2035" : "#ffffff",
                  color: darkMode ? "#ffffff !important" : `${textColor} !important`,
                },
                "& span": {
                  color: darkMode ? "#ffffff !important" : `${textColor} !important`,
                }
              },
              "& .rbc-agenda-view": {
                backgroundColor: darkMode ? "#202940" : "#ffffff",
                "& table": {
                  backgroundColor: darkMode ? "#202940" : "#ffffff",
                  borderColor: darkMode ? "#616161" : "#e0e0e0",
                },
                "& .rbc-agenda-date-cell, & .rbc-agenda-time-cell, & .rbc-agenda-event-cell": {
                  backgroundColor: darkMode ? "#202940" : "#ffffff",
                  color: darkMode ? "#ffffff !important" : `${textColor} !important`,
                  borderColor: darkMode ? "#616161" : "#e0e0e0",
                }
              },
              "& .rbc-month-view, & .rbc-time-view": {
                backgroundColor: darkMode ? "#202940" : "#ffffff",
                borderColor: darkMode ? "#616161" : "#e0e0e0",
                "& .rbc-day-bg": {
                  backgroundColor: darkMode ? "#202940" : "#ffffff",
                  borderColor: darkMode ? "#616161" : "#e0e0e0",
                },
                "& .rbc-today": {
                  backgroundColor: darkMode ? "#2d3748" : "#e3f2fd",
                },
                "& .rbc-off-range-bg": {
                  backgroundColor: darkMode ? "#1a2035" : "#f5f5f5",
                },
                "& .rbc-date-cell": {
                  color: darkMode ? "#ffffff" : textColor,
                },
                "& .rbc-time-slot": {
                  borderColor: darkMode ? "#616161" : "#e0e0e0",
                  color: darkMode ? "#ffffff" : textColor,
                },
                "& .rbc-time-content": {
                  borderColor: darkMode ? "#616161" : "#e0e0e0",
                },
                "& .rbc-timeslot-group": {
                  borderColor: darkMode ? "#616161" : "#e0e0e0",
                },
                "& .rbc-time-header": {
                  borderColor: darkMode ? "#616161" : "#e0e0e0",
                },
                "& .rbc-label": {
                  color: darkMode ? "#ffffff" : textColor,
                }
              }
            }}
          >
            {initialLoading ||
              !nodesList ||
              nodesList.length === 0 ||
              handleActivitiesList === null ||
              handleActivitiesList === undefined ? (
              <MDBox
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 500,
                }}>
                <span style={{ color: darkMode ? white : textColor }}>Cargando...</span>
              </MDBox>
            ) : (
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                defaultView={Views.AGENDA}
                views={[Views.AGENDA, Views.MONTH, Views.WEEK, Views.DAY]}
                length={365}
                onSelectEvent={handleEventClick}
                onView={(view) => setCurrentView(view)}
                formats={{
                  timeGutterFormat: (date, culture, localizer) =>
                    localizer.format(date, "HH:mm", culture),
                  eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
                    `${localizer.format(start, "HH:mm", culture)} – ${localizer.format(
                      end,
                      "HH:mm",
                      culture
                    )}`,
                }}
                components={{
                  agenda: {
                    event: AgendaEvent,
                    time: ({ event }) => (
                      <span style={{ color: darkMode ? white : textColor }}>
                        {dayjs(event.start).format("HH:mm")} – {dayjs(event.end).format("HH:mm")}
                      </span>
                    ),
                  },
                }}
                messages={messages}
                style={{ height: 500, width: "100%" }}
              />
            )}
          </MDBox>
        </>
      )}
      {/* Modal de eventos */}
      {selectedEvent && (
        <EventModal open={modalOpen} onClose={handleCloseModal} event={selectedEvent} />
      )}
      {valueTab === 1 && (
        <>
          <div style={{ width: "100%" }}>
            <DataTable
              table={tableData}
              canSearch={false}
              isSorted={true}
              entriesPerPage={false}
              showTotalEntries={false}
              pagination={{ variant: "gradient", color: "info" }}
            />
          </div>
          {!handleActivitiesList ? (
            <MDBox style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <MDTypography
                style={{ left: "20px", fontSize: "20px" }}
                variant="button"
                color="secondary"
                fontWeight="regular">
                No hay datos
              </MDTypography>
            </MDBox>
          ) : (
            <>
              {/* Mostrar la paginación */}
              <MDBox style={{ marginLeft: "20px" }} mb={{ xs: 3, sm: 0 }}>
                <MDTypography
                  style={{ left: "20px" }}
                  variant="button"
                  fontWeight="regular">
                  Mostrando {entriesStart} hasta {entriesEnd} de {totalData} resultados en total
                </MDTypography>
              </MDBox>
              <Stack style={{ marginLeft: "20px", marginBottom: "10px" }} spacing={2}>
                <DACIPagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              </Stack>{" "}
            </>
          )}
        </>
      )}



      {/* Contenedor para la paginación */}
      {/* <MDBox style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </MDBox> */}
    </div>
  );
};

export default ActivitiesList;
